import React, { useCallback, useMemo } from 'react';

import useContactForm from '../hooks/useContactForm';
import ContactFormFields from '../ContactFormFields/ContactFormFields';

import { FIELD_NAMES } from '../common/fields';
import { StoreAddress } from '@/shared/organisms/StoreSelector/StoreSelector';
import Button from '@/shared/molecules/Button';

import constants from '@/shared/constants/contact-us-page';

import css from './ContactForm.module.css';

export const FIELDS_NEEDED_TO_SUBMIT = 3;
export interface ContactFormProps {
  onFormSubmitted?: (withError?: Error) => void;
}

const ContactForm = ({
  onFormSubmitted
}: ContactFormProps): JSX.Element | null => {
  const [saving, setSaving] = React.useState(false);

  const {
    control,
    trigger,
    register,
    errors,
    handleSubmit,
    fieldsByFieldset,
    getSibilingFieldByName,
    isFieldNextToAnother,
    onSelectionChange,
    currentFieldset,
    getValues,
    formState,
    setError
  } = useContactForm({
    formId: 'contact-form'
  });

  const onSubmit = useCallback(async (data: Record<string, string>) => {
    if (formState?.isSubmitting || !formState?.isValid || !data || saving) {
      return;
    }

    // check email and confirm email match
    if (data?.[FIELD_NAMES.EMAIL] !== data?.[FIELD_NAMES.CONFIRM_EMAIL]) {
      setError?.(FIELD_NAMES.CONFIRM_EMAIL, {
        type: 'manual',
        message: 'Emails do not match'
      });

      return;
    }

    setSaving(true);

    const store = data?.[FIELD_NAMES.STORE_LOCATION] as StoreAddress;
    const { storeNumber = '' } = store || {};

    const record = {
      storeNumber,
      reason: data?.[FIELD_NAMES.MAIN_REASON],
      serviceType: data?.[FIELD_NAMES.ORDER_METHOD],
      accessType: data?.[FIELD_NAMES.DEVICE_USED],
      visitDate: data?.[FIELD_NAMES.ORDER_DATE],
      visitTime: data?.[FIELD_NAMES.ORDER_TIME],
      amountSpent: data?.[FIELD_NAMES.AMOUNT_SPENT],
      notes: data?.[FIELD_NAMES.SHARED_FEEDBACK],
      contactBy: data?.[FIELD_NAMES.RESPONSE_METHOD],
      firstName: data?.[FIELD_NAMES.FIRST_NAME],
      lastName: data?.[FIELD_NAMES.LAST_NAME],
      email: data?.[FIELD_NAMES.EMAIL],
      dayPhone: data?.[FIELD_NAMES.PHONE],
      hutRewardsIssue: data?.[FIELD_NAMES.REWARDS]
    };

    try {
      const response = await fetch(constants.SUBMIT_SERVICE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(record)
      });

      if (!response.ok) {
        throw new Error('Network response was not ok while submitting contact form');
      }

      await response.json();

      setSaving(false);
      onFormSubmitted?.();
      return;
    } catch (error) {
      setSaving(false);
      setError?.(FIELD_NAMES.MAIN_REASON, {
        type: 'manual',
        message: 'Failed to submit form, please try again later'
      });
      onFormSubmitted?.(error instanceof Error ? error : new Error('Unknown error from submit contact form'));
    }
  }, [
    saving,
    setSaving,
    setError,
    onFormSubmitted,
    formState?.isSubmitting,
    formState?.isValid
  ]);

  const showSubmitButton = useMemo((): boolean => fieldsByFieldset?.length >= FIELDS_NEEDED_TO_SUBMIT, [fieldsByFieldset]);

  return (
    <form
      data-testid="contact-form"
      onSubmit={handleSubmit(onSubmit)}
      className={css.form}
    >
      <ContactFormFields
        control={control}
        trigger={trigger}
        register={register}
        errors={errors}
        handleSubmit={handleSubmit}
        fieldsByFieldset={fieldsByFieldset}
        getSibilingFieldByName={getSibilingFieldByName}
        isFieldNextToAnother={isFieldNextToAnother}
        onSelectionChange={onSelectionChange}
        currentFieldset={currentFieldset}
        getValues={getValues}
      />
      {showSubmitButton ? (
        <Button
          id="contact-form-submit-button"
          type="submit"
          label="Submit"
          size="medium"
          color="secondary"
          variantStyle="contained"
          disabled={!formState?.isValid || formState?.isSubmitting || saving}
          loading={formState?.isSubmitting || saving}
          fullWidth
          classes={{
            root: css.submitButton
          }}
        />
      ) : null}
    </form>
  );
};

export default ContactForm;
